import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ITradedAccount,
  IGetFundedTraderDataResponse,
  IGetFundedAccountNotesResponse,
  IFundedTrader,
  IFundedAccountNote,
} from "../../api/funds/index.types";
import { IGetCustomerEvaluationCompleteHistoryResponse } from "../../api/evaluations/index.types";
import { FundedAccountDocumentDates } from "../../pages/FundedTrader/components/DocumentDatesForm";
import { checkIsCompletedAccount } from "../../pages/FundedTrader/utils";
// import { IFundedTraderState } from "./index.types";

export interface IFundedTraderState extends IFundedTrader {
  tradedAccounts: {
    data: ITradedAccount[];
    isLoading: boolean;
  };
  documentsDates: {
    data: FundedAccountDocumentDates;
    isLoading: boolean;
  };
  isLoading: boolean;
  fundedAccounts: ITradedAccount[];
  priorAccounts: ITradedAccount[];
  account: {
    name: string;
    email: string;
  };
  notes: {
    data: IFundedAccountNote[];
    count: number;
    isLoading: boolean;
  };
  net_withdrawal_amount: number;
  evaluationCompletedHistory: {
    data: IGetCustomerEvaluationCompleteHistoryResponse["data"]["rows"];
    count: number;
    isLoading: boolean;
  };
  evaluationCompletedActiveOrRecentAccounts: {
    data: IGetCustomerEvaluationCompleteHistoryResponse["data"]["rows"];
    count: number;
    isLoading: boolean;
  };
}

const initialState: IFundedTraderState = {
  tradedAccounts: {
    data: [],
    isLoading: true,
  },
  documentsDates: {
    isLoading: false,
    data: {
      address_proof: null,
      w8or9: null,
      personal_id: null,
    },
  },
  isLoading: false,
  fundedAccounts: [],
  priorAccounts: [],
  accounts_id: -1,
  id: 0,
  timestamp: "",
  account: {
    name: "",
    email: "",
  },
  notes: {
    data: [],
    count: 0,
    isLoading: false,
  },
  is_business_ein: false,
  business_ein_name: null,
  start_trading_date: null,
  is_professional_live_sim: false,
  is_priority_to_process: false,
  e2t_interview: null,
  on_vacation_until: null,
  e2t_affiliate_value: null,
  net_withdrawal_amount: 0,
  is_forced_to_live: false,
  pep: "no",
  watermark: 0,
  livesim_net_withdrawal_amount: 0,
  passes_before_envoy: 0,
  withdrawals_before_envoy: 0,
  total_withdrawn_before_envoy: 0,
  funded_trader_status: {
    id: 1,
    status_name: "Recent Passer",
    status: "recent_passer",
  },
  ban_date: null,
  evaluation_type: null,
  invoice_status: null,
  processing_status: null,
  evaluationCompletedHistory: {
    data: [],
    count: 0,
    isLoading: false,
  },
  evaluationCompletedActiveOrRecentAccounts: {
    data: [],
    count: 0,
    isLoading: false,
  },
  evaluation_completed_histories: [],
};

const fundedTraderSlice = createSlice({
  name: "fundedTrader",
  initialState,
  reducers: {
    // TraderAccounts
    accountsFetchRequest: (state) => {
      state.tradedAccounts.isLoading = true;
    },
    accountsFetchSuccess: (state, action: PayloadAction<ITradedAccount[]>) => {
      state.tradedAccounts.isLoading = false;
      state.tradedAccounts.data = action.payload;

      const fundedAccounts: ITradedAccount[] = [];
      const priorAccounts: ITradedAccount[] = [];

      action.payload.forEach((account) => {
        if (
          checkIsCompletedAccount(
            account.processing_status,
            account.original_account,
            account,
            action.payload
          )
        ) {
          priorAccounts.push(account);
        } else {
          fundedAccounts.push(account);
        }
      });

      state.priorAccounts = priorAccounts;
      state.fundedAccounts = fundedAccounts;
    },
    accountsFetchFailed: (state) => {
      state.tradedAccounts.isLoading = false;
    },

    // TraderData
    dataFetchRequest: (state) => {
      state.isLoading = true;
    },
    dataFetchSuccess: (state, action: PayloadAction<IGetFundedTraderDataResponse["data"]>) => {
      Object.assign(state, action.payload.funded_trader);
      state.net_withdrawal_amount =
        action.payload.forced_to_live?.rows[0]?.net_withdrawal_amount || 0;
      state.isLoading = false;
    },
    dataFetchFailed: (state) => {
      state.isLoading = false;
    },

    // TraderDocumentsDates
    documentsDatesFetchRequest: (state) => {
      state.documentsDates.isLoading = true;
    },
    documentsDatesFetchSuccess: (state, action: PayloadAction<FundedAccountDocumentDates>) => {
      state.documentsDates.data = action.payload;
      state.documentsDates.isLoading = false;
    },
    documentsDatesFetchFailed: (state) => {
      state.documentsDates.isLoading = false;
    },

    // TraderNotes
    notesFetchRequest: (state) => {
      state.notes.isLoading = true;
    },
    notesFetchSuccess: (state, action: PayloadAction<IGetFundedAccountNotesResponse>) => {
      state.notes.data = action.payload.data.notes;
      state.notes.count = action.payload.data.count;
      state.notes.isLoading = false;
    },
    notesFetchFailed: (state) => {
      state.notes.isLoading = false;
    },

    // EvaluationCompletedHistory
    evaluationCompletedHistoryFetchRequest: (state) => {
      state.evaluationCompletedHistory.isLoading = true;
    },
    evaluationCompletedHistoryFetchSuccess: (
      state,
      action: PayloadAction<IGetCustomerEvaluationCompleteHistoryResponse["data"]>
    ) => {
      state.evaluationCompletedHistory.data = action.payload.rows;
      state.evaluationCompletedHistory.count = action.payload.count;
      state.evaluationCompletedHistory.isLoading = false;
    },
    evaluationCompletedHistoryFetchFailed: (state) => {
      state.evaluationCompletedHistory.isLoading = false;
    },

    // EvaluationCompletedActiveAndRecentAccounts
    passesActiveAndRecentAccountsFetchRequest: (state) => {
      state.evaluationCompletedActiveOrRecentAccounts.isLoading = true;
    },
    passesActiveAndRecentAccountsFetchSuccess: (
      state,
      action: PayloadAction<IGetCustomerEvaluationCompleteHistoryResponse["data"]>
    ) => {
      state.evaluationCompletedActiveOrRecentAccounts.data = action.payload.rows;
      state.evaluationCompletedActiveOrRecentAccounts.count = action.payload.count;
      state.evaluationCompletedActiveOrRecentAccounts.isLoading = false;
    },
    passesActiveAndRecentAccountsFetchFailed: (state) => {
      state.evaluationCompletedActiveOrRecentAccounts.isLoading = false;
    },
  },
});

export const fundedTraderActions = fundedTraderSlice.actions;
export default fundedTraderSlice.reducer;
